import { ChangeDetectionStrategy, Component, DoCheck, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatSelectModule } from '@angular/material/select';
import { ConfirmActionModalComponent } from '../confirm-action-modal/confirm-action-modal.component';
import { FamilyMemberFormComponent } from '../family-member-form/family-member-form.component';
import type { FamilyFormOption } from '../../types/family-member-form.types';
import { US_STATES } from '../../constants/us-states.constant';
import type { ExistingMemberCandidate, FamilyAddContext } from '../../types/family-members.types';
import { FamilyMembersFlowController } from '../../utils/family-members-flow.controller';
import {
  NewPlateForm,
  buildExistingDraft,
  buildNewPlateDraft,
  cardLabel,
  needsSavedCard,
  resultSummary,
  takenPlatesOf,
  totalChargedNow,
  vehicleCounter,
} from '../../utils/family-members.util';
import { formatFamilyValue } from '../../utils/family-plan-settings.util';
import {
  FamilyMemberErrors,
  formatFamilyMoney,
  previewMemberPrice,
} from '../../../components/notification-modal/utils/notification-family-sale.util';

type MembersTab = 'new' | 'existing';

const DEFAULT_STATE = 'CA';

const emptyForm = (discountValue: number | null, state: string): NewPlateForm => ({
  firstName: '',
  lastName: '',
  phone: '',
  plate: '',
  state,
  packageId: '',
  discountValue,
});

/**
 * "Add family members": the step after Start Family (CWS-1581). Line up new vehicles (charged to
 * the primary's saved card) and existing members (discount from their next renewal), then add
 * them with the primary's code, or without it when the primary's car is at the lane. Members can
 * also be skipped and added later. The host owns the flow (see FamilyMembersFlowController); this
 * component keeps only the form fields and renders the flow's state.
 *
 * @selector app-family-members-modal
 * @input flow - The add-members flow; the modal renders while it is open.
 */
@Component({
  selector: 'app-family-members-modal',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatSelectModule,
    ConfirmActionModalComponent,
    FamilyMemberFormComponent,
  ],
  templateUrl: './family-members-modal.component.html',
  styleUrls: ['./family-members-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class FamilyMembersModalComponent implements DoCheck {
  @Input() flow!: FamilyMembersFlowController;

  readonly states = US_STATES;
  readonly money = formatFamilyMoney;

  tab: MembersTab = 'new';
  form: NewPlateForm = emptyForm(null, DEFAULT_STATE);
  errors: FamilyMemberErrors = {};
  searchText = '';
  selected: ExistingMemberCandidate | null = null;
  existingDiscount: number | null = null;

  private seenContext: FamilyAddContext | null = null;
  private optionsFor: FamilyAddContext | null = null;
  private packageOptionsCache: FamilyFormOption[] = [];
  private discountOptionsCache: FamilyFormOption<number>[] = [];

  /** A freshly loaded context resets the form to the location's default discount. */
  ngDoCheck(): void {
    const context = this.flow?.context ?? null;
    if (context !== this.seenContext) {
      this.seenContext = context;
      const fallback = context?.settings.default_discount_value ?? null;
      this.form = emptyForm(fallback, this.primaryState);
      this.existingDiscount = fallback;
      this.errors = {};
      this.searchText = '';
      this.selected = null;
      this.tab = context && !this.flow.canAddNewVehicle ? 'existing' : 'new';
    }
  }

  get context(): FamilyAddContext | null {
    return this.flow.context;
  }

  get title(): string {
    switch (this.flow.step) {
      case 'otp':
        return 'Confirm with the primary';
      case 'done':
        return 'Family members';
      default:
        return this.flow.justStarted ? 'Family Plan started' : 'Add family members';
    }
  }

  /** New plates are usually from the primary's state; California only when it is not known. */
  private get primaryState(): string {
    const state = this.flow.subject?.state?.trim().toUpperCase();
    return state && this.states.some(s => s.code === state) ? state : DEFAULT_STATE;
  }

  get confirmLabel(): string {
    switch (this.flow.step) {
      case 'otp':
        return 'Add members';
      case 'done':
        return 'Close';
      default: {
        const count = this.flow.drafts.length;
        return count === 1 ? 'Add 1 member' : count > 1 ? `Add ${count} members` : 'Add members';
      }
    }
  }

  get cancelLabel(): string {
    switch (this.flow.step) {
      case 'otp':
        return 'Back';
      case 'done':
        return 'Try the failed ones again';
      default:
        return this.flow.drafts.length ? 'Cancel' : 'Skip for now';
    }
  }

  get showCancel(): boolean {
    return this.flow.step !== 'done' || this.flow.anyFailed;
  }

  get confirmDisabled(): boolean {
    switch (this.flow.step) {
      case 'otp':
        return !this.flow.canSubmitCode;
      case 'done':
        return false;
      default:
        return !this.flow.drafts.length || this.flow.loading || !!this.flow.loadError;
    }
  }

  get counter(): string {
    return this.context ? vehicleCounter(this.context, this.flow.drafts.length) : '';
  }

  get cardText(): string | null {
    if (!this.context) return null;
    return (
      cardLabel(this.context.card) ?? (this.context.card_unavailable ? 'the saved card' : null)
    );
  }

  get presets(): number[] {
    return this.context?.settings.presets ?? [];
  }

  /** The form's package list, rebuilt only when a new context loads. */
  get packageOptions(): FamilyFormOption[] {
    const context = this.context;
    if (this.optionsFor !== context) {
      this.refreshOptions(context);
    }
    return this.packageOptionsCache;
  }

  get discountOptions(): FamilyFormOption<number>[] {
    const context = this.context;
    if (this.optionsFor !== context) {
      this.refreshOptions(context);
    }
    return this.discountOptionsCache;
  }

  private refreshOptions(context: FamilyAddContext | null): void {
    this.optionsFor = context;
    this.packageOptionsCache = (context?.packages ?? []).map(item => ({
      value: item.id,
      label: this.packageLabel(item),
    }));
    this.discountOptionsCache = this.presets.map(value => ({
      value,
      label: this.discountLabel(value),
    }));
  }

  get chargedNow(): number {
    return totalChargedNow(this.flow.drafts);
  }

  get needsCard(): boolean {
    return needsSavedCard(this.flow.drafts);
  }

  get summary(): string {
    return resultSummary(this.flow.outcomes);
  }

  /** The new-vehicle price shown under the form, once a package and discount are picked. */
  get formPrice(): { recurringPrice: number; list: number } | null {
    const context = this.context;
    const pkg = context?.packages.find(item => item.id === this.form.packageId);
    if (!context || !pkg || this.form.discountValue === null) {
      return null;
    }
    const price = previewMemberPrice(
      pkg.list_price,
      context.settings.discount_type,
      this.form.discountValue
    );
    return { recurringPrice: price.recurringPrice, list: pkg.list_price };
  }

  /** The existing member's price from the next renewal, once a discount is picked. */
  get existingPrice(): number | null {
    const context = this.context;
    if (!context || !this.selected || this.existingDiscount === null) {
      return null;
    }
    return previewMemberPrice(
      this.selected.price,
      context.settings.discount_type,
      this.existingDiscount
    ).recurringPrice;
  }

  discountLabel(value: number): string {
    return formatFamilyValue(this.context?.settings.discount_type ?? null, value);
  }

  packageLabel(item: { name: string; list_price: number }): string {
    return `${item.name} · ${formatFamilyMoney(item.list_price)}/mo`;
  }

  setTab(tab: MembersTab): void {
    this.tab = tab;
    this.errors = {};
  }

  addNewVehicle(): void {
    const context = this.context;
    if (!context) {
      return;
    }
    const primaryPlate = this.flow.subject?.plate ?? '';
    const { errors, item } = buildNewPlateDraft(
      this.form,
      context,
      takenPlatesOf(primaryPlate, this.flow.drafts),
      crypto.randomUUID()
    );
    this.errors = errors;
    if (item && this.flow.addDraft(item)) {
      this.form = emptyForm(context.settings.default_discount_value, this.primaryState);
    }
  }

  runSearch(): void {
    this.selected = null;
    this.flow.search(this.searchText);
  }

  pick(candidate: ExistingMemberCandidate): void {
    if (candidate.eligible) {
      this.selected = candidate;
    }
  }

  addExisting(): void {
    const context = this.context;
    if (!context || !this.selected || this.existingDiscount === null) {
      return;
    }
    const item = buildExistingDraft(
      this.selected,
      this.existingDiscount,
      context,
      crypto.randomUUID()
    );
    if (this.flow.addDraft(item)) {
      this.selected = null;
      this.searchText = '';
      this.flow.clearCandidates();
    }
  }

  onConfirm(): void {
    switch (this.flow.step) {
      case 'add':
        this.flow.proceed();
        break;
      case 'otp':
        this.flow.submit();
        break;
      default:
        this.flow.close();
    }
  }

  onCancel(): void {
    switch (this.flow.step) {
      case 'otp':
        this.flow.backToAdd();
        break;
      case 'done':
        if (this.flow.anyFailed) {
          this.flow.retryFailed();
        } else {
          this.flow.close();
        }
        break;
      default:
        this.flow.close();
    }
  }
}
